import {CommonModule, CurrencyPipe} from '@angular/common';
import {Component, inject, OnDestroy, OnInit, DestroyRef} from '@angular/core';
import {
    DxChartModule,
    DxPieChartModule
} from 'devextreme-angular';
import {
    AccessType,
    AuthService,
    GetYearToDateSummaryQuery,  
    IMyScoreShiftQuery,
    PermissionType,
    ProductivityClient,
    YearToDateScoreExportQuery,
    YearToDateScoreQuery,
    YearToDateSummaryDto
} from "@services";
import {catchError, distinctUntilChanged, switchMap, map, tap, shareReplay} from "rxjs/operators";
import {EMPTY, BehaviorSubject, combineLatest, Observable} from "rxjs";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {ScoreDashboardComponent} from "../../../../components/library/score-dashboard/score-dashboard.component";
import { CardItem, SummaryCardComponent } from 'src/app/pages/attendance/my-score/summary-card/summary-card.component';

export function yearToDatePalette(): Record<string, string> {
    const getColor = (name: string) => getComputedStyle(document.body).getPropertyValue(name).trim();
    return {
        ["Inbound Volume Cases"]: getColor('--bs-info'),
        ["Outbound Volume Cases"]: getColor('--bs-success'),
    };
}

export function yearToDateSummaryCards(summary: YearToDateSummaryDto): CardItem[] {
    return [
        {
            label: 'Inbound Volume Cases',
            subLabel: '',
            value: summary.inboundVolumeCases,
            icon: 'fa-light fa-cube',
            color: 'text-info',
            bg: 'bg-info-subtle'
        },
        {
            label: 'Outbound Volume Cases',
            subLabel: '',
            value: summary.outboundVolumeCases,
            icon: 'fa-light fa-cube',
            color: 'text-success',
            bg: 'bg-success-subtle'
        },
        {
            label: 'Case Pick Volume',
            subLabel: '',
            value: summary.casePickVolume,
            icon: 'fa-light fa-cube',
            color: 'text-warning',
            bg: 'bg-warning-subtle'
        },
        {
            label: 'Inbound Volume Pallets',
            subLabel: '',
            value: summary.inboundVolumePallets,
            icon: 'fa-light fa-right-to-bracket',
            color: 'text-info',
            bg: 'bg-info-subtle'
        },
        {
            label: 'Outbound Volume Pallets',
            subLabel: '',
            value: summary.outboundVolumePallets,
            icon: 'fa-thin fa-right-from-bracket',
            color: 'text-success',
            bg: 'bg-success-subtle'
        },
        {
            label: 'Layer Pick Volume',
            subLabel: '',
            value: summary.layerPickVolume,
            icon: 'fa-thin fa-layer-group',
            color: 'text-warning',
            bg: 'bg-warning-subtle'
        },
    ];
}

@Component({
    selector: 'app-year-to-date-score',
    imports: [CommonModule, DxPieChartModule, DxChartModule, SummaryCardComponent, ScoreDashboardComponent],
    providers: [CurrencyPipe],
    templateUrl: './year-to-date-score.component.html',
})
export class YearToDateScoreComponent implements OnInit, OnDestroy {
    private productivityClient = inject(ProductivityClient);
    private destroyRef = inject(DestroyRef);
    protected authService = inject(AuthService);

    protected readonly PermissionType = PermissionType;
    protected readonly AccessType = AccessType;

    summaryCards: CardItem[] = [];
    palette: Record<string, string> = {};

    private readonly dateRange$ = new BehaviorSubject<[Date, Date]>([new Date(), new Date()]);
    summary$: Observable<any>;

    loadScoreData = (query: IMyScoreShiftQuery) => {
        return this.productivityClient.getYearToDateScore(new YearToDateScoreQuery(query));
    }

    ngOnInit(): void {
        this.palette = yearToDatePalette();

        this.summary$ = combineLatest([this.dateRange$]).pipe(
            map(([range]) => ({from: range?.[0], to: range?.[1]})),
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
            switchMap(({from, to}) => {
                if (from) {
                    const query = new GetYearToDateSummaryQuery({from, to});
                    return this.productivityClient.getYearToDateSummary(query).pipe(
                        catchError(() => this.empty())
                    );
                }
                return this.empty();
            }),
            tap(summary => {
                this.summaryCards = [
                    ...yearToDateSummaryCards(summary),
                    {
                        label: '',
                        subLabel: '',
                        value: null,
                        icon: '',
                        color: '',
                        bg: ''
                    },
                    {
                        label: 'MPS Pallets',
                        subLabel: '',
                        value: summary.mpsPallets,
                        icon: 'fa-thin fa-layer-group',
                        color: 'text-success',
                        bg: 'bg-success-subtle'
                    },
                ];
            }),
            shareReplay(1),
            takeUntilDestroyed(this.destroyRef)
        );
    }

    ngOnDestroy(): void {
        this.dateRange$.complete();
    }

    empty() {
        this.summaryCards = this.summaryCards.map(card => ({...card, value: 0}));
        return EMPTY;
    }

    exportData = (from: Date, to: Date,) => {
        const query = new YearToDateScoreExportQuery({from, to});
        return this.productivityClient.exportYearToDateScore(query);
    }

    onDateRangeChanged(range: [Date, Date]) {
        this.dateRange$.next(range);
    }
}
